import React, { useState, useEffect } from "react";
import { LOGO_URL } from "../data/mockData";
import {
  adminLogin, adminUpdatePresale, adminUpdateTokenomics, adminUpdateContent,
  adminUpdateSocialLinks, adminUpdateDepositWallets, adminUpdateSiteConfig,
  adminGetOrders, adminUpdateOrder, adminGetUsers, adminGetAirdropClaims,
  fetchPresale, fetchTokenomics, fetchContent, fetchSocialLinks,
  fetchDepositWallets, fetchSiteConfig, fetchAirdropConfig,
  adminUpdateAirdropConfig, adminUpdateUser, adminDeleteUser, adminChangePassword,
} from "../services/api";
import {
  LayoutDashboard, Settings, Coins, FileText, Link2, ShieldCheck,
  LogOut, Trash2, Plus, Save, BarChart3, DollarSign, TrendingUp,
  ChevronRight, Users, Package, Gift, Edit2, Key,
} from "lucide-react";

const TABS = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { id: "presale", label: "Presale", icon: Coins },
  { id: "tokenomics", label: "Tokenomics", icon: BarChart3 },
  { id: "content", label: "Content", icon: FileText },
  { id: "social", label: "Social Links", icon: Link2 },
  { id: "wallets", label: "Wallets", icon: ShieldCheck },
  { id: "orders", label: "Orders", icon: Package },
  { id: "users", label: "Users", icon: Users },
  { id: "settings", label: "Settings", icon: Settings },
];

const AdminPanel = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loginForm, setLoginForm] = useState({ username: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [activeTab, setActiveTab] = useState("dashboard");
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [saveMsg, setSaveMsg] = useState("");
  const [saving, setSaving] = useState(false);

  const [presale, setPresale] = useState({});
  const [tokenomics, setTokenomics] = useState({});
  const [content, setContent] = useState({});
  const [socialLinks, setSocialLinks] = useState({});
  const [depositWallets, setDepositWallets] = useState([]);
  const [siteSettings, setSiteSettings] = useState({});
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [airdropClaims, setAirdropClaims] = useState([]);
  const [editingUser, setEditingUser] = useState(null);
  const [editBalance, setEditBalance] = useState("");
  const [passwordForm, setPasswordForm] = useState({ currentPassword: "", newPassword: "", confirmPassword: "" });
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");

  useEffect(() => {
    const token = localStorage.getItem("mrs_admin_token");
    if (token) { setIsLoggedIn(true); loadAllData(); }
  }, []);

  const loadAllData = async () => {
    try {
      const [ps, tk, ct, sl, dw, sc, ords, usrs, ac] = await Promise.all([
        fetchPresale().catch(() => ({})), fetchTokenomics().catch(() => ({})),
        fetchContent().catch(() => ({})), fetchSocialLinks().catch(() => ({})),
        fetchDepositWallets().catch(() => ({})), fetchSiteConfig().catch(() => ({})),
        adminGetOrders().catch(() => []), adminGetUsers().catch(() => []),
        adminGetAirdropClaims().catch(() => []),
      ]);
      setPresale({ ...ps, endDate: ps.endDate ? ps.endDate.slice(0, 16) : "" });
      setTokenomics(tk);
      setContent(ct);
      setSocialLinks(sl);
      setDepositWallets(dw.wallets || []);
      setSiteSettings(sc);
      setOrders(ords);
      setUsers(usrs);
      setAirdropClaims(ac);
    } catch (e) { console.error(e); }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const res = await adminLogin({ email: loginForm.username, password: loginForm.password });
      localStorage.setItem("mrs_admin_token", res.token);
      setIsLoggedIn(true);
      setLoginError("");
      loadAllData();
    } catch (err) {
      setLoginError(err.response?.data?.detail || "Invalid credentials");
    }
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    localStorage.removeItem("mrs_admin_token");
  };

  const saveData = async (saveFn, data, msg) => {
    setSaving(true);
    try {
      await saveFn(data);
      setSaveMsg(msg || "Saved!");
      setTimeout(() => setSaveMsg(""), 3000);
    } catch (e) {
      setSaveMsg("Error: " + (e.response?.data?.detail || "Save failed"));
      setTimeout(() => setSaveMsg(""), 3000);
    } finally { setSaving(false); }
  };

  const handleUpdateOrder = async (orderId, status) => {
    try {
      await adminUpdateOrder(orderId, { status });
      setOrders(orders.map(o => o.id === orderId ? { ...o, status } : o));
    } catch (e) { console.error(e); }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setEditBalance(user.holdings || 0);
  };

  const handleSaveUserBalance = async () => {
    if (!editingUser) return;
    try {
      await adminUpdateUser(editingUser.id, { holdings: parseFloat(editBalance) });
      setUsers(users.map(u => u.id === editingUser.id ? { ...u, holdings: parseFloat(editBalance) } : u));
      setEditingUser(null);
      setSaveMsg("User balance updated!");
      setTimeout(() => setSaveMsg(""), 3000);
    } catch (e) {
      setSaveMsg("Error: " + (e.response?.data?.detail || "Update failed"));
      setTimeout(() => setSaveMsg(""), 3000);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this user? This will also delete all their orders and claims.")) return;
    try {
      await adminDeleteUser(userId);
      setUsers(users.filter(u => u.id !== userId));
      setSaveMsg("User deleted!");
      setTimeout(() => setSaveMsg(""), 3000);
    } catch (e) {
      setSaveMsg("Error: " + (e.response?.data?.detail || "Delete failed"));
      setTimeout(() => setSaveMsg(""), 3000);
    }
  };

  const handleChangeAdminPassword = async (e) => {
    e.preventDefault();
    setPasswordError("");
    setPasswordSuccess("");
    
    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      setPasswordError("Please fill all fields");
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      setPasswordError("New password must be at least 6 characters");
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError("New passwords do not match");
      return;
    }

    try {
      await adminChangePassword({
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      });
      setPasswordSuccess("Password changed successfully!");
      setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
      setTimeout(() => setPasswordSuccess(""), 3000);
    } catch (e) {
      setPasswordError(e.response?.data?.detail || "Password change failed");
    }
  };

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-[#060d06] flex items-center justify-center p-4">
        <div className="w-full max-w-sm">
          <div className="text-center mb-8">
            <img src={LOGO_URL} alt="Logo" className="w-20 h-20 rounded-full mx-auto mb-4 border-2 border-yellow-400" />
            <h1 className="text-2xl font-bold text-white">Admin Panel</h1>
            <p className="text-gray-500 text-sm">MARSUPILAMI COIN Management</p>
          </div>
          <form onSubmit={handleLogin} className="bg-[#0d1a0d] border border-[#1a3a1a] rounded-xl p-6">
            {loginError && <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4 text-red-400 text-sm">{loginError}</div>}
            <div className="mb-4">
              <label className="text-gray-400 text-sm mb-1 block">Username</label>
              <input type="text" value={loginForm.username} onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })} className="w-full bg-[#060d06] border border-[#1a3a1a] rounded-lg px-3 py-2.5 text-white focus:border-yellow-400/50 focus:outline-none text-sm" placeholder="Enter username" />
            </div>
            <div className="mb-6">
              <label className="text-gray-400 text-sm mb-1 block">Password</label>
              <input type="password" value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} className="w-full bg-[#060d06] border border-[#1a3a1a] rounded-lg px-3 py-2.5 text-white focus:border-yellow-400/50 focus:outline-none text-sm" placeholder="••••••" />
            </div>
            <button type="submit" className="w-full py-3 rounded-xl font-bold text-black text-sm bg-gradient-to-r from-yellow-400 to-amber-500 hover:from-yellow-300 hover:to-amber-400 transition-all">LOGIN</button>
          </form>
        </div>
      </div>
    );
  }

  const InputField = ({ label, value, onChange, type = "text", placeholder = "" }) => (
    <div className="mb-4">
      <label className="text-gray-400 text-xs mb-1 block">{label}</label>
      <input type={type} value={value || ""} onChange={onChange} placeholder={placeholder} className="w-full bg-[#060d06] border border-[#1a3a1a] rounded-lg px-3 py-2 text-white focus:border-yellow-400/50 focus:outline-none text-sm" />
    </div>
  );

  const TextAreaField = ({ label, value, onChange, rows = 3 }) => (
    <div className="mb-4">
      <label className="text-gray-400 text-xs mb-1 block">{label}</label>
      <textarea value={value || ""} onChange={onChange} rows={rows} className="w-full bg-[#060d06] border border-[#1a3a1a] rounded-lg px-3 py-2 text-white focus:border-yellow-400/50 focus:outline-none text-sm resize-none" />
    </div>
  );

  const renderDashboard = () => (
    <div>
      <h2 className="text-xl font-bold text-white mb-6">Dashboard</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {[
          { label: "Total Sales", value: `$${Number(presale.raised || 0).toLocaleString()}`, icon: DollarSign, color: "text-green-400" },
          { label: "Target", value: `$${Number(presale.target || 0).toLocaleString()}`, icon: TrendingUp, color: "text-yellow-400" },
          { label: "Token Price", value: `$${presale.price || 0}`, icon: Coins, color: "text-blue-400" },
          { label: "Total Users", value: users.length, icon: Users, color: "text-purple-400" },
        ].map((stat) => (
          <div key={stat.label} className="bg-[#0d1a0d] border border-[#1a3a1a] rounded-xl p-5">
            <div className="flex items-center justify-between mb-3"><span className="text-gray-400 text-sm">{stat.label}</span><stat.icon size={18} className={stat.color} /></div>
            <p className="text-white text-xl font-bold">{stat.value}</p>
          </div>
        ))}
      </div>
      <div className="bg-[#0d1a0d] border border-[#1a3a1a] rounded-xl p-5">
        <h3 className="text-white font-bold mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[{ label: "Edit Presale", tab: "presale" }, { label: "Manage Orders", tab: "orders" }, { label: "Manage Content", tab: "content" }].map((action) => (
            <button key={action.tab} onClick={() => setActiveTab(action.tab)} className="flex items-center justify-between bg-[#060d06] border border-[#1a3a1a] rounded-lg px-4 py-3 text-gray-300 hover:text-yellow-400 hover:border-yellow-400/30 transition-all text-sm">
              {action.label}<ChevronRight size={16} />
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  const renderPresale = () => (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-white">Presale Management</h2>
        <button onClick={() => saveData(adminUpdatePresale, { ...presale, id: "presale_config" }, "Presale updated!")} disabled={saving} className="flex items-center gap-2 bg-gradient-to-r from-yellow-400 to-amber-500 text-black font-bold text-sm px-4 py-2 rounded-lg hover:from-yellow-300 hover:to-amber-400 transition-all disabled:opacity-50"><Save size={16} /> Save Presale</button>
      </div>
      <div className="bg-[#0d1a0d] border border-[#1a3a1a] rounded-xl p-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <InputField label="Token Price ($)" value={presale.price} onChange={(e) => setPresale({ ...presale, price: e.target.value })} />
          <InputField label="End Date (Countdown)" type="datetime-local" value={presale.endDate} onChange={(e) => setPresale({ ...presale, endDate: e.target.value })} />
          <InputField label="Raised ($)" value={presale.raised} onChange={(e) => setPresale({ ...presale, raised: e.target.value })} />
          <InputField label="Target ($)" value={presale.target} onChange={(e) => setPresale({ ...presale, target: e.target.value })} />
          <InputField label="Tokens For Sale" value={presale.tokensForSale} onChange={(e) => setPresale({ ...presale, tokensForSale: e.target.value })} />
          <div className="flex items-center gap-3 mt-6">
            <label className="text-gray-400 text-sm">Presale Active:</label>
            <button onClick={() => setPresale({ ...presale, isLive: !presale.isLive })} className={`w-12 h-6 rounded-full transition-all duration-300 ${presale.isLive ? "bg-green-500" : "bg-gray-600"}`}>
              <div className={`w-5 h-5 rounded-full bg-white transition-transform duration-300 ${presale.isLive ? "translate-x-6" : "translate-x-0.5"}`} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderTokenomics = () => (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-white">Tokenomics Management</h2>
        <button onClick={() => saveData(adminUpdateTokenomics, { ...tokenomics, id: "tokenomics" }, "Tokenomics updated!")} disabled={saving} className="flex items-center gap-2 bg-gradient-to-r from-yellow-400 to-amber-500 text-black font-bold text-sm px-4 py-2 rounded-lg hover:from-yellow-300 hover:to-amber-400 transition-all disabled:opacity-50"><Save size={16} /> Save</button>
      </div>
      <div className="bg-[#0d1a0d] border border-[#1a3a1a] rounded-xl p-6 mb-6">
        <InputField label="Contract Address" value={tokenomics.contractAddress} onChange={(e) => setTokenomics({ ...tokenomics, contractAddress: e.target.value })} />
        <InputField label="Total Supply" value={tokenomics.totalSupply} onChange={(e) => setTokenomics({ ...tokenomics, totalSupply: e.target.value })} />
      </div>
      <div className="bg-[#0d1a0d] border border-[#1a3a1a] rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-bold">Distribution</h3>
          <button onClick={() => setTokenomics({ ...tokenomics, distribution: [...(tokenomics.distribution || []), { name: "New", percentage: 0, color: "#666" }] })} className="flex items-center gap-1 text-yellow-400 text-sm hover:text-yellow-300"><Plus size={14} /> Add</button>
        </div>
        {(tokenomics.distribution || []).map((item, i) => (
          <div key={i} className="flex items-center gap-3 mb-3">
            <input type="color" value={item.color} onChange={(e) => { const u = [...tokenomics.distribution]; u[i] = { ...u[i], color: e.target.value }; setTokenomics({ ...tokenomics, distribution: u }); }} className="w-8 h-8 rounded cursor-pointer bg-transparent border-0" />
            <input value={item.name} onChange={(e) => { const u = [...tokenomics.distribution]; u[i] = { ...u[i], name: e.target.value }; setTokenomics({ ...tokenomics, distribution: u }); }} className="flex-1 bg-[#060d06] border border-[#1a3a1a] rounded-lg px-3 py-2 text-white text-sm focus:border-yellow-400/50 focus:outline-none" />
            <input type="number" value={item.percentage} onChange={(e) => { const u = [...tokenomics.distribution]; u[i] = { ...u[i], percentage: Number(e.target.value) }; setTokenomics({ ...tokenomics, distribution: u }); }} className="w-20 bg-[#060d06] border border-[#1a3a1a] rounded-lg px-3 py-2 text-white text-sm focus:border-yellow-400/50 focus:outline-none" />
            <span className="text-gray-400 text-sm">%</span>
            <button onClick={() => setTokenomics({ ...tokenomics, distribution: tokenomics.distribution.filter((_, idx) => idx !== i) })} className="text-red-400 hover:text-red-300"><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
    </div>
  );

  const renderContent = () => (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-white">Content Management</h2>
        <button onClick={() => saveData(adminUpdateContent, { ...content, id: "content" }, "Content updated!")} disabled={saving} className="flex items-center gap-2 bg-gradient-to-r from-yellow-400 to-amber-500 text-black font-bold text-sm px-4 py-2 rounded-lg hover:from-yellow-300 hover:to-amber-400 transition-all disabled:opacity-50"><Save size={16} /> Save</button>
      </div>
      <div className="bg-[#0d1a0d] border border-[#1a3a1a] rounded-xl p-6 mb-6">
        <h3 className="text-yellow-400 font-bold mb-4">About Section</h3>
        <InputField label="Title" value={content.about?.title} onChange={(e) => setContent({ ...content, about: { ...content.about, title: e.target.value } })} />
        <TextAreaField label="Description" value={content.about?.description} onChange={(e) => setContent({ ...content, about: { ...content.about, description: e.target.value } })} rows={4} />
      </div>
      <div className="bg-[#0d1a0d] border border-[#1a3a1a] rounded-xl p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-yellow-400 font-bold">Features</h3>
          <button onClick={() => setContent({ ...content, features: [...(content.features || []), { title: "New Feature", description: "Description", icon: "Shield" }] })} className="flex items-center gap-1 text-yellow-400 text-sm hover:text-yellow-300"><Plus size={14} /> Add</button>
        </div>
        {(content.features || []).map((f, i) => (
          <div key={i} className="border border-[#1a3a1a] rounded-lg p-4 mb-3">
            <div className="flex items-center justify-between mb-2"><span className="text-gray-400 text-xs">Feature {i + 1}</span><button onClick={() => setContent({ ...content, features: content.features.filter((_, idx) => idx !== i) })} className="text-red-400 hover:text-red-300"><Trash2 size={14} /></button></div>
            <input value={f.title} onChange={(e) => { const u = [...content.features]; u[i] = { ...u[i], title: e.target.value }; setContent({ ...content, features: u }); }} className="w-full bg-[#060d06] border border-[#1a3a1a] rounded-lg px-3 py-2 text-white text-sm mb-2 focus:border-yellow-400/50 focus:outline-none" placeholder="Title" />
            <textarea value={f.description} onChange={(e) => { const u = [...content.features]; u[i] = { ...u[i], description: e.target.value }; setContent({ ...content, features: u }); }} rows={2} className="w-full bg-[#060d06] border border-[#1a3a1a] rounded-lg px-3 py-2 text-white text-sm resize-none focus:border-yellow-400/50 focus:outline-none" placeholder="Description" />
          </div>
        ))}
      </div>
      <div className="bg-[#0d1a0d] border border-[#1a3a1a] rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-yellow-400 font-bold">FAQ</h3>
          <button onClick={() => setContent({ ...content, faq: [...(content.faq || []), { question: "New Question?", answer: "Answer" }] })} className="flex items-center gap-1 text-yellow-400 text-sm hover:text-yellow-300"><Plus size={14} /> Add</button>
        </div>
        {(content.faq || []).map((f, i) => (
          <div key={i} className="border border-[#1a3a1a] rounded-lg p-4 mb-3">
            <div className="flex items-center justify-between mb-2"><span className="text-gray-400 text-xs">Question {i + 1}</span><button onClick={() => setContent({ ...content, faq: content.faq.filter((_, idx) => idx !== i) })} className="text-red-400 hover:text-red-300"><Trash2 size={14} /></button></div>
            <input value={f.question} onChange={(e) => { const u = [...content.faq]; u[i] = { ...u[i], question: e.target.value }; setContent({ ...content, faq: u }); }} className="w-full bg-[#060d06] border border-[#1a3a1a] rounded-lg px-3 py-2 text-white text-sm mb-2 focus:border-yellow-400/50 focus:outline-none" placeholder="Question" />
            <textarea value={f.answer} onChange={(e) => { const u = [...content.faq]; u[i] = { ...u[i], answer: e.target.value }; setContent({ ...content, faq: u }); }} rows={2} className="w-full bg-[#060d06] border border-[#1a3a1a] rounded-lg px-3 py-2 text-white text-sm resize-none focus:border-yellow-400/50 focus:outline-none" placeholder="Answer" />
          </div>
        ))}
      </div>
    </div>
  );

  const renderSocial = () => (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-white">Social Media Links</h2>
        <button onClick={() => saveData(adminUpdateSocialLinks, { ...socialLinks, id: "social_links" }, "Social links updated!")} disabled={saving} className="flex items-center gap-2 bg-gradient-to-r from-yellow-400 to-amber-500 text-black font-bold text-sm px-4 py-2 rounded-lg hover:from-yellow-300 hover:to-amber-400 transition-all disabled:opacity-50"><Save size={16} /> Save</button>
      </div>
      <div className="bg-[#0d1a0d] border border-[#1a3a1a] rounded-xl p-6">
        {["telegram", "twitter", "discord", "instagram"].map((key) => (
          <InputField key={key} label={key === 'twitter' ? 'X (Twitter)' : key.charAt(0).toUpperCase() + key.slice(1)} value={socialLinks[key]} onChange={(e) => setSocialLinks({ ...socialLinks, [key]: e.target.value })} placeholder={`https://${key}.com/...`} />
        ))}
      </div>
    </div>
  );

  const renderWallets = () => (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-white">Deposit Wallets</h2>
        <button onClick={() => saveData(adminUpdateDepositWallets, { id: "deposit_wallets", wallets: depositWallets }, "Wallets updated!")} disabled={saving} className="flex items-center gap-2 bg-gradient-to-r from-yellow-400 to-amber-500 text-black font-bold text-sm px-4 py-2 rounded-lg hover:from-yellow-300 hover:to-amber-400 transition-all disabled:opacity-50"><Save size={16} /> Save</button>
      </div>
      <div className="bg-[#0d1a0d] border border-[#1a3a1a] rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <p className="text-gray-400 text-sm">Manage deposit wallet addresses.</p>
          <button onClick={() => setDepositWallets([...depositWallets, { name: "New Wallet", address: "0x..." }])} className="flex items-center gap-1 text-yellow-400 text-sm hover:text-yellow-300"><Plus size={14} /> Add</button>
        </div>
        {depositWallets.map((w, i) => (
          <div key={i} className="border border-[#1a3a1a] rounded-lg p-4 mb-3">
            <div className="flex items-center justify-between mb-2"><span className="text-gray-400 text-xs">Wallet {i + 1}</span><button onClick={() => setDepositWallets(depositWallets.filter((_, idx) => idx !== i))} className="text-red-400 hover:text-red-300"><Trash2 size={14} /></button></div>
            <input value={w.name} onChange={(e) => { const u = [...depositWallets]; u[i] = { ...u[i], name: e.target.value }; setDepositWallets(u); }} className="w-full bg-[#060d06] border border-[#1a3a1a] rounded-lg px-3 py-2 text-white text-sm mb-2 focus:border-yellow-400/50 focus:outline-none" placeholder="Wallet Name" />
            <input value={w.address} onChange={(e) => { const u = [...depositWallets]; u[i] = { ...u[i], address: e.target.value }; setDepositWallets(u); }} className="w-full bg-[#060d06] border border-[#1a3a1a] rounded-lg px-3 py-2 text-white text-sm font-mono focus:border-yellow-400/50 focus:outline-none" placeholder="0x..." />
          </div>
        ))}
      </div>
    </div>
  );

  const renderOrders = () => (
    <div>
      <h2 className="text-xl font-bold text-white mb-6">Orders Management</h2>
      <div className="bg-[#0d1a0d] border border-[#1a3a1a] rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead><tr className="border-b border-[#1a3a1a]">
              <th className="text-left text-gray-400 text-xs uppercase px-4 py-3">ID</th>
              <th className="text-left text-gray-400 text-xs uppercase px-4 py-3">User</th>
              <th className="text-left text-gray-400 text-xs uppercase px-4 py-3">Amount</th>
              <th className="text-left text-gray-400 text-xs uppercase px-4 py-3">Tokens</th>
              <th className="text-left text-gray-400 text-xs uppercase px-4 py-3">TX Hash</th>
              <th className="text-left text-gray-400 text-xs uppercase px-4 py-3">Status</th>
              <th className="text-left text-gray-400 text-xs uppercase px-4 py-3">Actions</th>
            </tr></thead>
            <tbody>
              {orders.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">No orders yet.</td></tr>
              ) : orders.map(o => (
                <tr key={o.id} className="border-b border-[#1a3a1a] hover:bg-[#1a3a1a]/50">
                  <td className="px-4 py-3 text-white text-xs font-mono">#{o.id?.slice(-6)}</td>
                  <td className="px-4 py-3 text-white text-xs">{o.username}</td>
                  <td className="px-4 py-3 text-white text-xs">${o.amount}</td>
                  <td className="px-4 py-3 text-white text-xs">{(o.tokensPurchased || 0).toLocaleString()}</td>
                  <td className="px-4 py-3 text-gray-400 text-xs font-mono truncate max-w-[100px]">{o.txHash}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-bold px-2 py-1 rounded-full ${o.status === 'Approved' ? 'bg-green-400/10 text-green-400' : o.status === 'Rejected' ? 'bg-red-400/10 text-red-400' : 'bg-yellow-400/10 text-yellow-400'}`}>{o.status}</span>
                  </td>
                  <td className="px-4 py-3 flex gap-2">
                    <button onClick={() => handleUpdateOrder(o.id, 'Approved')} className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded hover:bg-green-500/30">Approve</button>
                    <button onClick={() => handleUpdateOrder(o.id, 'Rejected')} className="text-xs bg-red-500/20 text-red-400 px-2 py-1 rounded hover:bg-red-500/30">Reject</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderUsers = () => (
    <div>
      <h2 className="text-xl font-bold text-white mb-6">User Management ({users.length})</h2>
      <div className="bg-[#0d1a0d] border border-[#1a3a1a] rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead><tr className="border-b border-[#1a3a1a]">
              <th className="text-left text-gray-400 text-xs uppercase px-4 py-3">Username</th>
              <th className="text-left text-gray-400 text-xs uppercase px-4 py-3">Email</th>
              <th className="text-left text-gray-400 text-xs uppercase px-4 py-3">Full Name</th>
              <th className="text-left text-gray-400 text-xs uppercase px-4 py-3">MRS Balance</th>
              <th className="text-left text-gray-400 text-xs uppercase px-4 py-3">Registered</th>
              <th className="text-left text-gray-400 text-xs uppercase px-4 py-3">Actions</th>
            </tr></thead>
            <tbody>
              {users.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-500">No users yet.</td></tr>
              ) : users.map(u => (
                <tr key={u.id} className="border-b border-[#1a3a1a] hover:bg-[#1a3a1a]/50">
                  <td className="px-4 py-3 text-yellow-400 text-sm">{u.username}</td>
                  <td className="px-4 py-3 text-white text-sm">{u.email}</td>
                  <td className="px-4 py-3 text-white text-sm">{u.fullName}</td>
                  <td className="px-4 py-3 text-green-400 text-sm font-bold">{(u.holdings || 0).toLocaleString()} MRS</td>
                  <td className="px-4 py-3 text-gray-400 text-sm">{new Date(u.createdAt).toLocaleDateString()}</td>
                  <td className="px-4 py-3 flex gap-2">
                    <button onClick={() => handleEditUser(u)} className="text-xs bg-blue-500/20 text-blue-400 px-3 py-1 rounded hover:bg-blue-500/30 flex items-center gap-1"><Edit2 size={12} /> Edit</button>
                    <button onClick={() => handleDeleteUser(u.id)} className="text-xs bg-red-500/20 text-red-400 px-3 py-1 rounded hover:bg-red-500/30 flex items-center gap-1"><Trash2 size={12} /> Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit User Balance Modal */}
      {editingUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={() => setEditingUser(null)}>
          <div className="bg-[#0d1a0d] border border-[#1a3a1a] rounded-xl w-full max-w-md p-6 relative" onClick={e => e.stopPropagation()}>
            <h3 className="text-white text-lg font-bold mb-4">Edit User Balance</h3>
            <p className="text-gray-400 text-sm mb-4">User: <span className="text-yellow-400">{editingUser.username}</span> ({editingUser.email})</p>
            <div className="mb-4">
              <label className="text-gray-400 text-xs mb-1 block">MRS Token Balance</label>
              <input 
                type="number" 
                value={editBalance} 
                onChange={(e) => setEditBalance(e.target.value)} 
                className="w-full bg-[#060d06] border border-[#1a3a1a] rounded-lg px-3 py-2.5 text-white focus:border-yellow-400/50 focus:outline-none text-sm" 
                placeholder="0" 
              />
            </div>
            <div className="flex gap-3">
              <button onClick={handleSaveUserBalance} className="flex-1 bg-gradient-to-r from-yellow-400 to-amber-500 text-black font-bold text-sm px-4 py-2.5 rounded-lg hover:from-yellow-300 hover:to-amber-400 transition-all">Save</button>
              <button onClick={() => setEditingUser(null)} className="flex-1 bg-gray-600 text-white font-bold text-sm px-4 py-2.5 rounded-lg hover:bg-gray-500 transition-all">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderSettings = () => (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-white">Site Settings</h2>
        <button onClick={() => saveData(adminUpdateSiteConfig, { ...siteSettings, id: "site_config" }, "Settings updated!")} disabled={saving} className="flex items-center gap-2 bg-gradient-to-r from-yellow-400 to-amber-500 text-black font-bold text-sm px-4 py-2 rounded-lg hover:from-yellow-300 hover:to-amber-400 transition-all disabled:opacity-50"><Save size={16} /> Save</button>
      </div>
      <div className="bg-[#0d1a0d] border border-[#1a3a1a] rounded-xl p-6 mb-6">
        <h3 className="text-yellow-400 font-bold mb-4">Site Configuration</h3>
        <InputField label="Coin Name" value={siteSettings.coinName} onChange={(e) => setSiteSettings({ ...siteSettings, coinName: e.target.value })} />
        <InputField label="Ticker" value={siteSettings.coinTicker} onChange={(e) => setSiteSettings({ ...siteSettings, coinTicker: e.target.value })} />
        <TextAreaField label="Tagline" value={siteSettings.tagline} onChange={(e) => setSiteSettings({ ...siteSettings, tagline: e.target.value })} rows={2} />
      </div>

      {/* Admin Password Change */}
      <div className="bg-[#0d1a0d] border border-[#1a3a1a] rounded-xl p-6">
        <div className="flex items-center gap-2 mb-4">
          <Key size={18} className="text-yellow-400" />
          <h3 className="text-yellow-400 font-bold">Change Admin Password</h3>
        </div>
        {passwordError && <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4 text-red-400 text-sm">{passwordError}</div>}
        {passwordSuccess && <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 mb-4 text-green-400 text-sm">{passwordSuccess}</div>}
        <form onSubmit={handleChangeAdminPassword}>
          <InputField 
            label="Current Password" 
            type="password"
            value={passwordForm.currentPassword} 
            onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })} 
            placeholder="Enter current password"
          />
          <InputField 
            label="New Password" 
            type="password"
            value={passwordForm.newPassword} 
            onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })} 
            placeholder="Enter new password (min 6 chars)"
          />
          <InputField 
            label="Confirm New Password" 
            type="password"
            value={passwordForm.confirmPassword} 
            onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })} 
            placeholder="Confirm new password"
          />
          <button type="submit" className="flex items-center gap-2 bg-gradient-to-r from-yellow-400 to-amber-500 text-black font-bold text-sm px-4 py-2.5 rounded-lg hover:from-yellow-300 hover:to-amber-400 transition-all">
            <Key size={16} /> Change Password
          </button>
        </form>
      </div>
    </div>
  );

  const tabContent = { dashboard: renderDashboard, presale: renderPresale, tokenomics: renderTokenomics, content: renderContent, social: renderSocial, wallets: renderWallets, orders: renderOrders, users: renderUsers, settings: renderSettings };

  return (
    <div className="min-h-screen bg-[#060d06] flex">
      <aside className={`${sidebarOpen ? 'w-56' : 'w-16'} bg-[#0d1a0d] border-r border-[#1a3a1a] flex flex-col transition-all duration-300 flex-shrink-0`}>
        <div className="p-4 border-b border-[#1a3a1a] flex items-center gap-3">
          <img src={LOGO_URL} alt="Logo" className="w-8 h-8 rounded-full flex-shrink-0" />
          {sidebarOpen && <span className="text-yellow-400 font-bold text-sm">MRS Admin</span>}
        </div>
        <nav className="flex-1 py-4">
          {TABS.map((tab) => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-all duration-200 ${activeTab === tab.id ? "text-yellow-400 bg-yellow-400/10 border-r-2 border-yellow-400" : "text-gray-400 hover:text-white hover:bg-white/5"}`}>
              <tab.icon size={18} className="flex-shrink-0" />
              {sidebarOpen && <span>{tab.label}</span>}
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-[#1a3a1a]">
          <button onClick={handleLogout} className="w-full flex items-center gap-3 text-red-400 hover:text-red-300 text-sm py-2"><LogOut size={18} className="flex-shrink-0" />{sidebarOpen && <span>Logout</span>}</button>
        </div>
      </aside>
      <main className="flex-1 flex flex-col min-h-screen">
        <header className="bg-[#0d1a0d] border-b border-[#1a3a1a] px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => setSidebarOpen(!sidebarOpen)} className="text-gray-400 hover:text-white"><LayoutDashboard size={20} /></button>
            <h1 className="text-white font-bold">{TABS.find((t) => t.id === activeTab)?.label}</h1>
          </div>
          <div className="flex items-center gap-3">
            {saveMsg && <span className={`text-sm animate-pulse ${saveMsg.startsWith('Error') ? 'text-red-400' : 'text-green-400'}`}>{saveMsg}</span>}
            <a href="/" className="text-gray-400 hover:text-yellow-400 text-sm">View Site</a>
          </div>
        </header>
        <div className="flex-1 p-6 overflow-auto">{tabContent[activeTab]?.()}</div>
      </main>
    </div>
  );
};

export default AdminPanel;
